
import {EventEmitter, Injectable} from '@angular/core';
import {LocalStorageService} from './local-storage.service';
import {Role} from '../models/user/role';
import {CookieService} from 'ngx-cookie-service';
import {PlatformConfigurationService} from './platform-configuration.service';
import {Observable, of} from 'rxjs';
import {map, tap} from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  loginOccurred = new EventEmitter<any>();

  constructor(private readonly localStorageService: LocalStorageService,
              private readonly platformConfigurationService: PlatformConfigurationService,
              private readonly cookieService: CookieService) {
  }

  logout() {
    this.localStorageService.clearUserData();
    this.cookieService.deleteAll();
    sessionStorage.clear();
  }

  isLoggedIn(): boolean {
    return !!this.getToken();
  }

  getToken(): string {
    return this.localStorageService.getToken();
  }

  getUsername(): string {
    const sub = this?.decodeToken()?.sub;
    if (sub != null)
      return sub;
    const username = this?.decodeToken()?.username;
    if (username != null)
      return username;
    return ''
  }

  getOrganisationName(): Observable<string> {
    if (!this.isLoggedIn()) {
      return of('');
    }
    if (this.localStorageService.getCentre() ) {
      return of(this.localStorageService.getCentre());
    }
    if (!this.getUsername().includes(':')) {
      return of('BURGER');
    }
    return this.platformConfigurationService.getUsersCentreName()
      .pipe(
        tap(response => this.localStorageService.setCentre(response.name)),
        map(response => response.name)
      );
  }

  getRoles(): string[] {
    return this.localStorageService.getRoles();
  }

  isSuperAdmin(): boolean {
    if (!this.isLoggedIn()) return false;
    return this.getRoles().includes(Role.SUPERADMIN_SCHAARBEEK);
  }

  isLocalAdmin(): boolean {
    if (!this.isLoggedIn()) return false;
    return this.getRoles()?.includes(Role.LOCAL_ADMIN) ?? false;
  }

  isAcademieAdmin(): boolean {
    return this.isSuperAdmin() || this.isLocalAdmin();
  }

  isMagazineAdmin(): boolean {
    if (!this.isLoggedIn()) return false;
    return this.getRoles().includes(Role.EMAGAZINE_ADMIN);
  }

  isMagazineReader(): boolean {
    if (!this.isLoggedIn()) return false;
    return this.getRoles().includes(Role.EMAGAZINE_READER);
  }

  isApprover(): boolean {
    if (!this.isLoggedIn()) return false;
    return this.getRoles().includes(Role.APPROVER_FREE_CLB);
  }

  isAdministrativeRole(): boolean {
    return this.isLoggedIn() && (this.isAcademieAdmin() || this.isApprover() || this.isMagazineAdmin());
  }

  isParticipant(): boolean {
    return this.isLoggedIn() &&
      (this.getRoles().includes(Role.PARTICIPANT_FREE) || this.getRoles().includes(Role.CLB_EXTERNAL_PARTICIPANT) || this.getRoles().includes(Role.CITIZEN));
  }

  isAcademie(): boolean {
    if (!this.isLoggedIn() || !this.getRoles()) return false;
    let hasRole = false;
    this.platformConfigurationService.getAcademieRoles().forEach(role => {
      if (this.getRoles().includes(role)) hasRole = true;
    })
    return hasRole;
  }

  isMagazine(): boolean {
    if (!this.isLoggedIn()) return false;
    let hasRole = false;
    this.platformConfigurationService.getMagazineRoles().forEach(role => {
      if (this.getRoles().includes(role)) hasRole = true;
    })
    return hasRole;
  }

  currentUserHasRole(role: string): boolean {
    return this.getRoles()?.includes(Role[role]) ?? false;
  }

  decodeToken(): any {
    if (this.getToken() == null) return null
    const jwtData = this.getToken().split('.')[1];
    const jwtFormattedData = jwtData ? jwtData.replace('-', '+').replace('_', '/') : null;
    const decodedJwtJsonData = atob(jwtFormattedData);
    return JSON.parse(decodedJwtJsonData);
  }

  notifyLoginOccurred() {
    this.loginOccurred.emit()
  }

  isExternalUser(): boolean {
    if (!this.isLoggedIn())
      return false;
    return this.getRoles().includes(Role.CLB_EXTERNAL_PARTICIPANT);
  }

  setLastLogin(lastLogin: Date) {
    this.localStorageService.setLastLogin(lastLogin);
  }

  getLastLogin(): Date {
    return this.localStorageService.getLastLogin();
  }

  isUnverified(): boolean {
    return this.getRoles().includes(Role.UNVERIFIED_USER);
  }

  isCitizen(): boolean {
    if (!this.isLoggedIn())
      return false;
    return this.getRoles().includes(Role.CITIZEN);
  }

  isExternalOrCitizen(): boolean {
    if (!this.isLoggedIn())
      return false;
    return this.getRoles().includes(Role.CLB_EXTERNAL_PARTICIPANT) || this.getRoles().includes(Role.CITIZEN);
  }

}
